import { useAppState } from '@/app/providers/AppStateProvider'
import { cn } from '@/shared/utils/cn'
import { Icon, type IconName } from '@/shared/ui/Icon'
import { ConversationSearch } from './ConversationSearch'
import { ConversationList } from './ConversationList'
import { UserProfileCard } from '@/features/workspace/components/UserProfileCard'
import { useConversations } from '../ConversationsProvider'
import { useAuth } from '@/features/auth'
import { mockWorkspace } from '@/features/workspace/mock'

interface PrimaryAction {
  key: string
  label: string
  icon: IconName
  onClick?: () => void
  title?: string
  active?: boolean
}

/**
 * Persistent left sidebar. Conversation rows come from the backend via
 * ConversationsProvider; the user card hydrates from /auth/me.
 */
export function LeftSidebar() {
  const {
    leftCollapsed,
    toggleLeft,
    openSettings,
    activeWorkspace,
    setWorkspace,
  } = useAppState()
  const { startNewConsultation } = useConversations()
  const { user, organization, role } = useAuth()

  const goToChat = () => setWorkspace('chat')
  const goToLibrary = () => setWorkspace('documents')
  const goToReferenceLibrary = () => setWorkspace('reference-library')

  const primaryActions: PrimaryAction[] = [
    {
      key: 'new-chat',
      label: 'Nova consulta',
      icon: 'plus',
      onClick: () => {
        goToChat()
        startNewConsultation()
      },
      title: 'Nova consulta',
      active: activeWorkspace === 'chat',
    },
    {
      key: 'library',
      label: 'Biblioteca',
      icon: 'doc',
      onClick: goToLibrary,
      title: 'Biblioteca de documentos',
      active: activeWorkspace === 'documents',
    },
    {
      key: 'reference-library',
      label: 'Referências',
      icon: 'book',
      onClick: goToReferenceLibrary,
      title: 'Biblioteca de Referência',
      active: activeWorkspace === 'reference-library',
    },
    {
      key: 'settings',
      label: 'Configurações',
      icon: 'settings',
      onClick: openSettings,
      title: 'Configurações',
    },
  ]

  const profileUser = user
    ? {
        id: user.id,
        fullName: user.full_name || user.email,
        initials: user.initials,
        role: organization?.display_name
          ? `${organization.display_name} · ${role ?? ''}`.trim().replace(/·\s*$/, '')
          : (role ?? ''),
        email: user.email,
      }
    : null

  return (
    <aside className={cn('sidebar', { collapsed: leftCollapsed })}>
      <div className="brand">
        <div className="brand-mark" aria-hidden="true" />
        {!leftCollapsed && (
          <div className="brand-text">
            <div className="brand-name">{organization?.display_name ?? mockWorkspace.brandName}</div>
            <div className="brand-tag">{organization?.slug ? `@${organization.slug}` : mockWorkspace.handle}</div>
          </div>
        )}
        <button
          type="button"
          className="brand-collapse"
          title={leftCollapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
          aria-label={leftCollapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
          onClick={toggleLeft}
        >
          <Icon name="collapse" />
        </button>
      </div>

      {!leftCollapsed && <ConversationSearch />}

      <div className="side-actions" role="group" aria-label="Ações">
        {primaryActions.map((action) => (
          <button
            key={action.key}
            type="button"
            data-testid={`nav-${action.key}`}
            className={`nav-item action${action.active ? ' active' : ''}`}
            onClick={action.onClick}
            title={action.title ?? action.label}
            aria-label={action.label}
            aria-current={action.active ? 'page' : undefined}
          >
            <Icon name={action.icon} className="action-icon" />
            {!leftCollapsed && <span className="label">{action.label}</span>}
          </button>
        ))}
      </div>

      {!leftCollapsed && <ConversationList />}

      <div className="sidebar-foot">
        {profileUser && (
          <UserProfileCard
            user={profileUser}
            onClick={openSettings}
            compact={leftCollapsed}
          />
        )}
      </div>
    </aside>
  )
}
